import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";

interface VersionInfo {
  version: string;
  timestamp?: number;
}

interface VersionCheckContextType {
  hasUpdate: boolean;
  isChecking: boolean;
  checkForUpdate: () => Promise<boolean>;
  refreshApp: (targetPath?: string) => void;
  navigateWithCheck: (path: string) => Promise<void>;
}

const VersionCheckContext = createContext<VersionCheckContextType | null>(null);

const RELOAD_COOLDOWN_KEY = "app-last-version-reload";
const POST_UPDATE_REDIRECT_KEY = "post-update-redirect";
const RELOAD_COOLDOWN_MS = 30 * 1000; // 30 seconds cooldown
const PERIODIC_CHECK_INTERVAL_MS = 30 * 60 * 1000; // 5 minutes

// Get the build version injected by vite-plugin-version-mark
const getBuildVersion = (): string | null => {
  try {
    return (
      (window as any).__SHOPEG_ADMIN_DEV_VERSION__ ||
      (window as any).__SHOPEG_ADMIN_PROD_VERSION__ ||
      null
    );
  } catch {
    return null;
  }
};

const fetchServerVersion = async (): Promise<VersionInfo | null> => {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

const canReload = (): boolean => {
  const lastReload = sessionStorage.getItem(RELOAD_COOLDOWN_KEY);
  if (!lastReload) return true;
  return Date.now() - parseInt(lastReload, 10) > RELOAD_COOLDOWN_MS;
};

const markReload = (): void => {
  sessionStorage.setItem(RELOAD_COOLDOWN_KEY, Date.now().toString());
};

export const VersionCheckProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const buildVersion = useRef<string | null>(getBuildVersion());
  const periodicCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Debug: Log on mount
  useEffect(() => {
    Sentry.addBreadcrumb({
      category: "version",
      message: "VersionCheck initialized",
      level: "info",
      data: { buildVersion: buildVersion.current },
    });
  }, []);

  const checkForUpdate = useCallback(async (): Promise<boolean> => {
    if (!buildVersion.current) {
      return false;
    }

    setIsChecking(true);

    try {
      const serverVersion = await fetchServerVersion();
      if (!serverVersion?.version) {
        return false;
      }

      const isOutdated = serverVersion.version !== buildVersion.current;

      if (isOutdated) {
        Sentry.addBreadcrumb({
          category: "version",
          message: "Version mismatch detected",
          level: "warning",
          data: {
            buildVersion: buildVersion.current,
            serverVersion: serverVersion.version,
          },
        });

        setHasUpdate(true);
        return true;
      }

      return false;
    } finally {
      setIsChecking(false);
    }
  }, []);

  const refreshApp = useCallback((targetPath?: string) => {
    if (canReload()) {
      markReload();
      if (targetPath) {
        sessionStorage.setItem(POST_UPDATE_REDIRECT_KEY, targetPath);
      }
      window.location.reload();
    }
  }, []);

  // Navigate with version check - checks BEFORE navigation
  const navigateWithCheck = useCallback(
    async (path: string): Promise<void> => {
      
      // Skip check if navigating to same path
      if (path === location.pathname) {
        return;
      }

      // If we already know there's an update, refresh immediately
      if (hasUpdate) {
        refreshApp(path);
        return;
      }

      const updateAvailable = await checkForUpdate();

      if (updateAvailable) {
        // Store target path and refresh
        refreshApp(path);
      } else {
        // No update, proceed with normal navigation
        navigate(path);
      }
    },
    [checkForUpdate, refreshApp, navigate, location.pathname, hasUpdate]
  );

  // Handle post-update redirect on mount
  useEffect(() => {
    const redirectPath = sessionStorage.getItem(POST_UPDATE_REDIRECT_KEY);
    if (redirectPath) {
      sessionStorage.removeItem(POST_UPDATE_REDIRECT_KEY);
      // Small delay to ensure app is fully loaded
      setTimeout(() => {
        navigate(redirectPath);
      }, 100);
    }
  }, [navigate]);

  // Periodic check every 5 minutes (only shows banner, no force refresh)
  useEffect(() => {
    
    // Run initial check
    checkForUpdate();
    
    periodicCheckRef.current = setInterval(() => {
      checkForUpdate();
    }, PERIODIC_CHECK_INTERVAL_MS);

    return () => {
      if (periodicCheckRef.current) {
        clearInterval(periodicCheckRef.current);
      }
    };
  }, [checkForUpdate]);

  return (
    <VersionCheckContext.Provider
      value={{ hasUpdate, isChecking, checkForUpdate, refreshApp, navigateWithCheck }}
    >
      {children}
    </VersionCheckContext.Provider>
  );
};

export const useVersionCheckContext = (): VersionCheckContextType => {
  const context = useContext(VersionCheckContext);
  if (!context) {
    throw new Error(
      "useVersionCheckContext must be used within a VersionCheckProvider"
    );
  }
  return context;
};
